import { CheckCircleOutlined } from "@ant-design/icons";
import { Checkbox } from "antd";
import { FC, useEffect, useState } from "react";
import { AppButton } from "site/ui";
import { useQuizStore } from "store";
import styled from "styled-components";
import { IQuestionAnswer } from "utils/types";

const QuestionContainer = styled.div`
  width: 100%;
  // min-width: 480px;
  padding: 24px 0;
  font-family: "Manrope", sans-serif;
`;

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const QuestionNumber = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: #111827;
  margin: 0;
`;

const TimeLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #6b7280;
`;

const TimerIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #e5e7eb;
  font-size: 12px;
  color: #6b7280;
`;

const QuestionText = styled.p`
  font-size: 16px;
  color: #111827;
  margin-bottom: 24px;
  line-height: 1.5;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
`;

const OptionLabel = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 8px;
`;

const Option = styled.div<{ selected?: boolean }>`
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.selected ? "#5575F7" : "#E5E7EB")};
  background-color: ${(props) => (props.selected ? "#F3F7FF" : "#FFFFFF")};
  font-size: 14px;
  color: #111827;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    border-color: ${(props) => (props.selected ? "#5575F7" : "#D1D5DB")};
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;
const ReasonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
`;

const ReasonLabel = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const Reason = styled.div`
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #E5E7EB;
  background-color: #dbffdb; // light green
  font-size: 14px;
  color: #111827;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  display: flex;
  align-items: center;
`;

export interface QuizActiveStateProps {
    onGetQuestionAnswer: (questionId: string) => Promise<IQuestionAnswer|undefined>
}
export const QuizActiveState:FC<QuizActiveStateProps> = ({
  onGetQuestionAnswer
}) => {
    const {
      quiz,
      quizMode, nextQuestion, previousQuestion, 
      questions, questionIndex, answerQuestion, 
      quizAnswers, quizTimerCountdown, completeQuiz
    } = useQuizStore();

    const [questionAnswer, setQuestionAnswer] = useState<IQuestionAnswer|undefined>(undefined);
    const [markedIds, setMarkedIds] = useState<string[]>([]);
    const question = questions[questionIndex]
    const formatTime = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m : ${remainingSeconds.toString().padStart(2, "0")}s`;
    }
    
    useEffect(() => {
      (async () => {
        const questionAnswer = await onGetQuestionAnswer(question.id)
        setQuestionAnswer(questionAnswer)
      })()
    }, [questionIndex])
    
    if (!question) return null;
    const isLastQuestion = questionIndex === (quiz?.totalQuestions || 0) - 1;
    const answer = quizAnswers?.find(item => item.questionId === question.id)
    const isMarked = markedIds.includes(question.id);

    return (
      <QuestionContainer>
        <QuestionHeader>
            <QuestionNumber>
            Question {questionIndex + 1} of {quiz?.totalQuestions || 0}
            </QuestionNumber>
            {quizMode === "timed" && (
            <TimeLeft>
                <TimerIcon>⏱</TimerIcon>
                Time left: {formatTime(quizTimerCountdown)}
            </TimeLeft>
            )}
        </QuestionHeader>

        <QuestionText>{question.content}</QuestionText>

        <OptionsContainer>
            <OptionLabel>Options {questionAnswer?.optionIds && questionAnswer.optionIds.length > 1 && `(Multiple Choice - ${questionAnswer.optionIds.length} answers)`}</OptionLabel>
            {question?.options?.map((option, index) => {
              const correct = questionAnswer?.optionIds.includes(option.id);
              return (
                <Option
                    key={index}
                    selected={Boolean(answer?.optionIds.includes(option.id))}
                    onClick={() => {
                      if (isMarked) return;
                      if(question.allowMultiple === "true"){
                        const answer = quizAnswers?.find(item => item.questionId === question.id)
                        if(answer){
                          if(answer.optionIds.includes(option.id)){
                            answerQuestion(question.id, answer.optionIds.filter(item => item !== option.id))
                          }else {
                            if(questionAnswer?.optionIds.length === answer.optionIds.length){
                              return;
                            }
                            answerQuestion(question.id, [...answer.optionIds, option.id])
                          }
                        }else {
                          answerQuestion(question.id, [option.id])
                        }
                      }else {
                        answerQuestion(question.id, [option.id])
                      }
                    }}
                >
                    {option.text}
                    {isMarked && correct && <CheckCircleOutlined style={{
                        // position: "fixed",
                        // right: "8px",
                        color: "green",
                        fontSize: 24,
                    }} size={42} color="green" />}
                </Option>
              );
            })}
        </OptionsContainer>
        
        {/* show reason if marked */}
        {isMarked && (
          <ReasonContainer>
            <ReasonLabel>Reason</ReasonLabel>
            <Reason>
              {questionAnswer?.explanation || "No reason provided"}
            </Reason>
          </ReasonContainer>
        )}
        <Checkbox 
          style={{marginBottom: 10}}
          checked={isMarked}
          disabled={isMarked || (!answer || answer.optionIds.length === 0)}
          onClick={() => {
            setMarkedIds(marked => [...marked, question.id])
          }}
        >
          Show answer(s)
        </Checkbox>
        <NavigationButtons>
            <AppButton
              variant="secondary"
              onClick={previousQuestion}
              style={{
                  maxWidth: "160px",
                  opacity: questionIndex === 0 ? 0.5 : 1,
                  pointerEvents: questionIndex === 0 ? "none" : "auto",
              }}
            >
              Previous
            </AppButton>
            <AppButton
                variant="primary"
                onClick={isLastQuestion ? completeQuiz : nextQuestion}
                style={{ maxWidth: "160px" }}
                disabled={!answer}
              >
                {isLastQuestion ? "Finish quiz" : "Next"}
            </AppButton>
        </NavigationButtons>
        </QuestionContainer>
    );
}